import {AfterViewInit, Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {BaseComponent} from '../../../base/base.component';
import {UserService} from '../../../service/user.service';
import {FilterService} from '../../../service/filter.service';
import {ExcelService} from '../../../service/excel.service';
import {
    CaronaBlocsAvailabilityGQL,
    CaronaVagaOcupadaGQL,
    RegisterCaronaEntryGQL,
    RegisterCaronaEntryInput,
    RegisterCaronaExitGQL,
    RegisterCaronaExitInput,
    SearchCaronaGQL,
    SearchCaronaInput,
    User
} from '../../../../generated/graphql';

declare var moment;

// Disponibilidade por bloco (shape enxuto da query CaronaBlocsAvailability).
interface BlocAvail {
    bloc: { id: string; name: string; capacity: number };
    capacity: number;
    occupied: number;
    available: number;
}

@Component({
    selector: 'app-carona-history',
    templateUrl: './carona-history.component.html',
    styleUrls: []
})
export class CaronaHistoryComponent extends BaseComponent implements OnInit, AfterViewInit {
    filterKey = 'carona_filter';
    user: User = new User();
    unityId: string;
    viewDashboard: boolean;

    filter: SearchCaronaInput = new SearchCaronaInput();
    result: any = {results: [], PageSize: 10, currentPage: 1, totalPage: 0, totalCount: 0};

    // Blocos com disponibilidade (select do modal de entrada manual).
    blocs: BlocAvail[] = [];

    // Correção manual (§46.6) — saída por linha / entrada geral.
    manualExit = {recordId: null as string, recordLabel: '', exitAt: '', exitNote: ''};
    manualEntry = {employeeName: '', re: '', licensePlate: '', blocId: null as string, vaga: '', entryAt: '', entryNote: ''};

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private searchCaronaGQL: SearchCaronaGQL,
                private entryGQL: RegisterCaronaEntryGQL,
                private exitGQL: RegisterCaronaExitGQL,
                private availabilityGQL: CaronaBlocsAvailabilityGQL,
                private vagaOcupadaGQL: CaronaVagaOcupadaGQL,
                public excelService: ExcelService,
                private filterService: FilterService) {
        super(router, translate);
    }

    // Exportação Excel (REST via ExcelService → string base64 → downloadHandlerFile).
    // O model inclui unity (escopo) — o filtro restaurado não carrega unity.
    actionExcel(): void {
        const model = {
            keyword: this.filter.keyword || null,
            status: this.filter.status || null,
            start: this.filter.start || null,
            end: this.filter.end || null,
            unity: this.unityId || null
        };
        this.excelService.getExcelCarona(model).subscribe({
            next: data => { super.downloadHandlerFile(data); },
            error: err => super.onError(err)
        });
    }

    // Fatia 1a — esta tela não tinha gating por perfil; criamos APENAS para o botão
    // Dashboard (admin/local-manager). Os demais botões seguem sem *ngIf (fora de escopo).
    actionUserProfileBTN() {
        switch (this.user.profile) {
            case 'admin':
                this.viewDashboard = true;
                break;
            case 'local-manager':
                this.viewDashboard = true;
                break;
            case 'local-operator':
                this.viewDashboard = false;
                break;
            case 'corporate-operator':
                this.viewDashboard = false;
                break;
            case 'vigilant':
                this.viewDashboard = false;
                break;
            case 'vigilant-leader':
                this.viewDashboard = false;
                break;
            case 'vigilant-conductor':
                this.viewDashboard = false;
                break;
            case 'lecturer':
                this.viewDashboard = false;
                break;
            case 'transport':
                this.viewDashboard = false;
                break;
            case 'user':
                this.viewDashboard = false;
                break;
        }
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
                this.actionUserProfileBTN();
                // Escopo: unidade do operador (mesmo padrão da tela de scan).
                this.unityId = this.user && this.user.unity ? this.user.unity.id : null;

                // Restaura filtros persistidos desta tela (FilterService).
                const saved: SearchCaronaInput = this.filterService.getFilter(this.filterKey);
                if (saved) {
                    this.filter = {
                        keyword: saved.keyword || null,
                        status: saved.status || null,
                        start: saved.start || null,
                        end: saved.end || null,
                        page: saved.page || 1
                    };
                }
                this.search();
                this.loadAvailability();
            }, error: err => super.onError(err)
        });
    }

    // Disponibilidade por bloco (mesma chamada da tela de scan).
    loadAvailability(): void {
        if (!this.unityId) {
            return;
        }
        this.availabilityGQL
            .fetch({unity: this.unityId}, {fetchPolicy: 'network-only'})
            .subscribe(({data}) => {
                this.blocs = (data.caronaBlocsAvailability as any[]) as BlocAvail[];
            }, err => super.onError(err));
    }

    ngAfterViewInit(): void {
        // Datepicker de intervalo — mesmo helper das outras listas (daterangepicker).
        this.setDatePicker('carona-daterange', (start, end) => {
            this.filter.start = start.utcOffset(0, true).format();
            this.filter.end = end.utcOffset(0, true).format();
        });
    }

    search(): void {
        const data: SearchCaronaInput = {
            keyword: this.filter.keyword || null,
            status: this.filter.status || null,
            start: this.filter.start || null,
            end: this.filter.end || null,
            unity: this.unityId || null,
            page: this.filter.page || 1
        };
        this.searchCaronaGQL
            .fetch({data}, {fetchPolicy: 'network-only'})
            .subscribe(({data: res}) => {
                this.result = res.searchCarona;
            }, err => super.onError(err));
    }

    onFilter(): void {
        this.filterService.setFilter(this.filterKey, this.filter);
        this.search();
    }

    clearFilter(): void {
        this.filterService.clearFilter(this.filterKey);
        this.filter = new SearchCaronaInput();
        this.filter.page = 1;
        this.search();
    }

    pageChanged(event: number): void {
        this.filter.page = event;
        this.onFilter();
    }

    // ---------- SAÍDA MANUAL (por linha, só registros 'inside') ----------
    openManualExit(record: any): void {
        if (record.status !== 'inside') {
            return;
        }
        this.manualExit = {
            recordId: record.id,
            recordLabel: `${record.employeeName || '-'} / ${record.re}`,
            exitAt: this.nowLocal(),
            exitNote: ''
        };
        super.showModal('historyManualExitModal');
    }

    submitManualExit(): void {
        if (super.isNullOrUndefined(this.manualExit.exitNote)) {
            super.showMessage(this.translate.instant('atencao'),
                this.translate.instant('justificativaobrigatoria'));
            return;
        }
        const data: RegisterCaronaExitInput = {
            record: {id: this.manualExit.recordId},
            exitAt: this.iso(this.manualExit.exitAt),
            isManualExit: true,
            exitNote: this.manualExit.exitNote
        };
        super.toggleLoader(true);
        this.exitGQL.mutate({data}).subscribe(() => {
            super.toggleLoader(false);
            super.closeModal('historyManualExitModal');
            super.showMessage(this.translate.instant('success'),
                this.translate.instant('saidaregistrada'), 'success');
            this.search();
            this.loadAvailability();
        }, err => {
            super.toggleLoader(false);
            super.onError(err);
        });
    }

    // Bloco selecionado no modal (para checar lotação no submit).
    private blocById(id: string): BlocAvail {
        return this.blocs.find(x => x.bloc.id === id);
    }

    // ---------- ENTRADA MANUAL (botão geral no topo) ----------
    openManualEntry(): void {
        this.manualEntry = {
            employeeName: '', re: '', licensePlate: '', blocId: null, vaga: '',
            entryAt: this.nowLocal(), entryNote: ''
        };
        // Recarrega a disponibilidade ao abrir (frescor + garante this.blocs
        // populado mesmo que o load do ngOnInit não tenha completado ainda).
        this.loadAvailability();
        super.showModal('historyManualEntryModal');
    }

    submitManualEntry(): void {
        // Coerência com a tela de scan: NOME opcional / RE obrigatório.
        const stringError = this.validateField([
            {value: this.manualEntry.re, text: `RE<br>`},
            {value: this.manualEntry.licensePlate, text: `${this.translate.instant('placa')}<br>`},
            {value: this.manualEntry.blocId, text: `${this.translate.instant('bloc')}<br>`},
            {value: this.manualEntry.entryNote, text: `${this.translate.instant('justificativa')}<br>`}
        ]);
        if (!super.isNullOrUndefined(stringError)) {
            super.showMessage(this.translate.instant('atencao'),
                `${this.translate.instant('preenchaoscampos')} <br> ${stringError}`);
            return;
        }
        // Vaga ocupada (aviso) → bloco lotado (aviso) → registra. Não trava, não duplica.
        this.checkVagaThen(this.manualEntry.vaga, () => {
            const b = this.blocById(this.manualEntry.blocId);
            if (b && b.available <= 0) {
                super.confirmMessage(this.translate.instant('atencao'),
                    this.translate.instant('blocolotadoconfirmar'),
                    () => this.proceedManualEntry());
                return;
            }
            this.proceedManualEntry();
        });
    }

    /**
     * Checagem de VAGA OCUPADA (§46 Fatia 2) — só AVISO, nunca trava. Vaga vazia
     * ou livre → chama cb direto; ocupada → confirma (SweetAlert) e só chama cb no
     * "Sim". cb encapsula o resto do fluxo (lotação + registro). Igual à tela de scan.
     */
    private checkVagaThen(vaga: string, cb: () => void): void {
        const v = (vaga || '').trim();
        if (!v) {
            cb();
            return;
        }
        this.vagaOcupadaGQL.fetch({unity: this.unityId, vaga: v}, {fetchPolicy: 'network-only'})
            .subscribe(({data}) => {
                if (data && data.caronaVagaOcupada) {
                    super.confirmMessage(this.translate.instant('atencao'),
                        this.translate.instant('vagaocupadaconfirmar', {vaga: v}), cb);
                } else {
                    cb();
                }
            }, err => super.onError(err));
    }

    private proceedManualEntry(): void {
        const data: RegisterCaronaEntryInput = {
            // Nome opcional: vazio → null.
            employeeName: this.manualEntry.employeeName || null,
            re: this.manualEntry.re,
            licensePlate: this.manualEntry.licensePlate,
            bloc: {id: this.manualEntry.blocId},
            // Vaga = texto livre (opcional).
            vaga: this.manualEntry.vaga || null,
            entryAt: this.iso(this.manualEntry.entryAt),
            isManualEntry: true,
            entryNote: this.manualEntry.entryNote
            // unity NÃO enviada — derivada do bloco no back-end.
        };
        super.toggleLoader(true);
        this.entryGQL.mutate({data}).subscribe(() => {
            super.toggleLoader(false);
            super.closeModal('historyManualEntryModal');
            super.showMessage(this.translate.instant('success'),
                this.translate.instant('entradaregistrada'), 'success');
            this.search();
            this.loadAvailability();
        }, err => {
            super.toggleLoader(false);
            super.onError(err);
        });
    }

    // ---------- utils (copiados da tela de scan) ----------
    iso(x: string): string {
        return x ? new Date(x).toISOString() : null;
    }

    nowLocal(): string {
        const d = new Date();
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
}
